import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  ShieldCheck, 
  ShieldAlert, 
  BadgeCheck, 
  Users, 
  FileCheck, 
  Activity, 
  HeartPulse, 
  Info 
} from "lucide-react";

const RadiationSafety = () => {
  return (
    <ServiceLayout
      title="Radiation Safety & Patient Protection"
      subtitle="Your health is our highest priority. We employ advanced protective protocols and AERB-certified technology to ensure minimal radiation exposure."
      heroImage="/images/hero/radiation-safety.jpg"
      images={["/images/safety-lead-apron.jpg", "/images/aerb-certificate.jpg"]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <div className="flex items-start gap-4 bg-green-50 p-6 rounded-2xl border-l-8 border-green-600">
          <ShieldCheck className="text-green-600 w-12 h-12 flex-shrink-0" />
          <p className="text-lg text-gray-700 leading-relaxed font-medium">
            At Dr. Sridhar International Dental Hospital & Research Centre, patient safety is not just a protocol—it is our highest priority. Every imaging procedure is performed with maximum protection and the highest standards of care.
          </p>
        </div>
      </section>

      {/* Primary Protective Gear Section */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
          <ShieldAlert className="text-blue-600" /> Essential Protective Gear
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-gray-100 p-8 rounded-3xl shadow-sm hover:shadow-md transition">
            <h4 className="text-xl font-bold text-blue-900 mb-3">Protective Thyroid Collars</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              The thyroid gland is highly sensitive to radiation. We provide every patient with a specialized <strong>Thyroid Collar</strong> to block any scattered radiation from reaching this vital area during X-ray procedures.
            </p>
          </div>
          <div className="bg-white border border-gray-100 p-8 rounded-3xl shadow-sm hover:shadow-md transition">
            <h4 className="text-xl font-bold text-blue-900 mb-3">Lead Aprons</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              To protect the rest of the body and other vital organs, we use high-grade <strong>Lead Aprons</strong>. These serve as an impenetrable barrier, ensuring that only the target dental area receives the necessary imaging energy.
            </p>
          </div>
        </div>
      </section>

      {/* Regulatory Compliance Block */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-16 shadow-xl relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
          <div className="md:w-1/4 flex justify-center">
            <BadgeCheck className="w-24 h-24 text-blue-300" />
          </div>
          <div className="md:w-3/4">
            <h2 className="text-3xl font-bold mb-4">AERB Certified Excellence</h2>
            <p className="text-blue-100 leading-relaxed mb-6">
              Our radiology department strictly follows all national radiation safety guidelines. We undergo annual <strong>AERB (Atomic Energy Regulatory Board)</strong> certification verification to ensure that all equipment and procedures meet rigorous national safety standards.
            </p>
            <div className="flex gap-4">
              <span className="bg-blue-800 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border border-blue-700">National Compliance</span>
              <span className="bg-blue-800 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border border-blue-700">Annual Audits</span>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Staff Protocols */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Continuous Safety Monitoring</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
            <Users className="text-blue-600 mb-4" />
            <h5 className="font-bold mb-2">Staff TLD Badges</h5>
            <p className="text-xs text-gray-600">Our staff wear Thermoluminescent Dosimeter (TLD) badges to continuously monitor and ensure their own exposure levels remain well below safety limits.</p>
          </div>
          <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
            <Activity className="text-blue-600 mb-4" />
            <h5 className="font-bold mb-2">ALARA Principle</h5>
            <p className="text-xs text-gray-600">We follow the 'As Low As Reasonably Achievable' principle, using high-sensitivity digital sensors to capture images with the least possible radiation.</p>
          </div>
          <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
            <FileCheck className="text-blue-600 mb-4" />
            <h5 className="font-bold mb-2">Verified Protocols</h5>
            <p className="text-xs text-gray-600">Every imaging procedure is cross-checked for accuracy to prevent the need for repeat X-rays, further minimizing exposure.</p>
          </div>
        </div>
      </section>

      {/* Safety Education footer */}
      <section className="border-t pt-12">
        <div className="flex flex-col md:flex-row gap-8 items-center bg-blue-50/50 p-8 rounded-3xl">
          <div className="md:w-2/3">
            <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" /> Why Digital Matters
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              By using modern <strong>Digital Radiology</strong> instead of traditional film X-rays, we reduce radiation exposure by up to <strong>80-90%</strong>. Our commitment to technology is a direct commitment to your long-term health and safety.
            </p>
          </div>
          <div className="md:w-1/3 text-center">
            <div className="inline-block p-4 bg-white rounded-full shadow-inner border border-blue-100">
              <HeartPulse className="w-12 h-12 text-red-500 animate-pulse" />
            </div>
            <p className="mt-2 text-xs font-bold text-gray-500 uppercase tracking-tighter">Maximum Protection</p>
          </div>
        </div>
      </section>
    </ServiceLayout>
  );
};

export default RadiationSafety;